import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import MicroCheckpoint from './MicroCheckpoint';
import { getVisualComponent } from './SVG_VISUALS';

export default function LevelView({
  level,
  userProgress = {},
  riskProfileFilled = false,
  initialCardIndex = 0,
  onBack = () => {},
  onCardViewed = () => {},
  onCheckpointAnswered = () => {},
  onLevelComplete = () => {},
  onNavigateToRiskProfile = () => {}
}) {
  const { colors } = useTheme();
  const [currentCardIndex, setCurrentCardIndex] = useState(initialCardIndex);
  const [showCheckpoint, setShowCheckpoint] = useState(false);
  const [activeCheckpoint, setActiveCheckpoint] = useState(null);
  const [answeredCheckpointIds, setAnsweredCheckpointIds] = useState(() => new Set());
  const [isLevelComplete, setIsLevelComplete] = useState(false);
  const [showRiskGate, setShowRiskGate] = useState(false);
  const cardTopRef = useRef(null);

  const cards = level?.cards || [];
  const totalCards = cards.length;

  // Scroll to top of card whenever card index changes (instant, no animation)
  useEffect(() => {
    cardTopRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
  }, [currentCardIndex]);

  const normalizedCheckpoints = useMemo(() => {
    const checkpoints = level?.checkpoints || [];
    return checkpoints.map((cp, index) => {
      const options = (cp.options || []).map((option, optionIndex) => {
        if (typeof option === 'string') {
          return { text: option, correct: optionIndex === cp.correct_index };
        }
        return { text: option.text, correct: !!option.correct };
      });
      return {
        ...cp,
        id: cp.checkpoint_index ?? index,
        options,
        explanation: cp.explanation || 'Review the card and concept once, then continue to build intuition.',
        after_card: Number(cp.after_card || 0)
      };
    });
  }, [level]);

  if (!level) return <div className="p-4">No level data</div>;

  if (totalCards === 0) {
    return (
      <div className="w-full max-w-2xl mx-auto px-3 py-3">
        <div className="app-card rounded-xl border p-4" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderColor: colors.border }}>
          <h3 className="text-xs font-semibold mb-1.5" style={{ color: colors.primaryText }}>{level.name}</h3>
          <p className="text-xs leading-relaxed" style={{ color: colors.secondaryText }}>
            This level is interactive and opens as practice mode. Continue to the next level when you are ready.
          </p>
          <div className="flex items-center gap-2 mt-5">
            <button onClick={onBack} className="px-3 py-1.5 rounded-lg text-xs font-semibold"
              style={{ backgroundColor: colors.background, color: colors.primaryText, border: `1px solid ${colors.border}` }}>
              Back to Levels
            </button>
            <button onClick={() => onLevelComplete(level.id)} className="px-3 py-1.5 rounded-lg text-xs font-semibold"
              style={{ backgroundColor: colors.accent, color: '#fff' }}>
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentCard = cards[currentCardIndex];
  const isLastCard = currentCardIndex === totalCards - 1;
  const riskGateIndex = level?.risk_gate_after_card ?? -1;

  const upcomingCheckpoint = normalizedCheckpoints.find(
    (cp) => cp.after_card === currentCardIndex + 1 && !answeredCheckpointIds.has(cp.id)
  );

  const handleAdvanceToNextCard = () => {
    if (isLastCard) {
      setIsLevelComplete(true);
      onLevelComplete(level.id);
      return;
    }
    setCurrentCardIndex((prev) => prev + 1);
  };

  const handleNextCard = () => {
    onCardViewed(level.id, currentCardIndex);
    if (riskGateIndex >= 0 && currentCardIndex === riskGateIndex && !riskProfileFilled) {
      setShowRiskGate(true);
      return;
    }
    if (upcomingCheckpoint && !showCheckpoint) {
      setActiveCheckpoint(upcomingCheckpoint);
      setShowCheckpoint(true);
      return;
    }
    handleAdvanceToNextCard();
  };

  const handlePrevCard = () => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex((prev) => prev - 1);
      setShowCheckpoint(false);
      setActiveCheckpoint(null);
    }
  };

  const handleCheckpointAnswer = (cpId, selectedOption) => {
    onCheckpointAnswered(level.id, cpId, selectedOption);
    setAnsweredCheckpointIds((prev) => { const next = new Set(prev); next.add(cpId); return next; });
    setShowCheckpoint(false);
    setActiveCheckpoint(null);
    handleAdvanceToNextCard();
  };

  if (showRiskGate) {
    return (
      <div className="w-full max-w-2xl mx-auto px-3 py-8">
        <div className="app-card rounded-xl border p-4 text-center" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderColor: colors.border }}>
          <div className="text-3xl mb-3">🛡️</div>
          <h2 className="text-sm font-bold mb-2" style={{ color: colors.primaryText }}>Complete Your Risk Profile First</h2>
          <p className="text-xs leading-relaxed mb-5" style={{ color: colors.secondaryText }}>
            Before continuing, you must set up your risk profile. This ensures your trades and practice sessions are calibrated to your actual risk tolerance  -  not a guess.
          </p>
          <button onClick={onNavigateToRiskProfile} className="w-full py-2.5 rounded-lg font-semibold text-xs mb-3"
            style={{ backgroundColor: colors.accent, color: '#fff' }}>
            Set Up My Risk Profile ->
          </button>
          <button onClick={() => setShowRiskGate(false)} className="w-full py-2 rounded-lg text-xs"
            style={{ backgroundColor: colors.background, color: colors.secondaryText, border: `1px solid ${colors.border}` }}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (showCheckpoint && activeCheckpoint) {
    return (
      <div className="w-full max-w-2xl mx-auto px-3 py-2">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold" style={{ color: colors.primaryText }}>Checkpoint</h3>
          <button
            onClick={() => { setShowCheckpoint(false); setActiveCheckpoint(null); handleAdvanceToNextCard(); }}
            className="px-3 py-1.5 rounded-lg transition-all text-xs"
            style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, color: colors.primaryText }}>
            Skip
          </button>
        </div>
        <MicroCheckpoint
          checkpoint={activeCheckpoint}
          onAnswered={(_, selectedOption) => handleCheckpointAnswer(activeCheckpoint.id, selectedOption)}
        />
      </div>
    );
  }

  if (isLevelComplete) {
    return <LevelCompleteScreen level={level} onBack={onBack} />;
  }

  // Dot indicators  -  show up to 7 dots, collapse middle ones with ellipsis beyond that
  const renderDots = () => {
    if (totalCards <= 1) return null;
    const dots = [];
    const maxDots = 7;
    if (totalCards <= maxDots) {
      for (let i = 0; i < totalCards; i++) {
        dots.push(
          <div key={i} className="rounded-full transition-all duration-200"
            style={{
              width: i === currentCardIndex ? 20 : 6,
              height: 6,
              backgroundColor: i === currentCardIndex ? colors.accent : `${colors.border}`,
              opacity: i === currentCardIndex ? 1 : 0.5,
            }} />
        );
      }
    } else {
      // Sliding window of 5 around current
      let start = Math.max(0, currentCardIndex - 2);
      let end = Math.min(totalCards - 1, start + 4);
      if (end - start < 4) start = Math.max(0, end - 4);
      if (start > 0) dots.push(<div key="s" style={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: colors.border, opacity: 0.4 }} />);
      for (let i = start; i <= end; i++) {
        dots.push(
          <div key={i} className="rounded-full transition-all duration-200"
            style={{
              width: i === currentCardIndex ? 20 : 6,
              height: 6,
              backgroundColor: i === currentCardIndex ? colors.accent : colors.border,
              opacity: i === currentCardIndex ? 1 : 0.5,
            }} />
        );
      }
      if (end < totalCards - 1) dots.push(<div key="e" style={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: colors.border, opacity: 0.4 }} />);
    }
    return dots;
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div ref={cardTopRef} className="flex items-center justify-between mb-3 px-3">
        <button onClick={onBack} className="app-card p-2 rounded-lg transition-all flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, color: colors.primaryText }} aria-label="Back">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="text-center flex-1 mx-2 min-w-0">
          <h3 className="text-sm font-semibold truncate" style={{ color: colors.primaryText }}>
            Level {level.display_id || level.id}: {level.name}
          </h3>
          <p className="text-xs" style={{ color: colors.secondaryText }}>
            {currentCardIndex + 1} / {totalCards}
          </p>
        </div>
        <button onClick={onBack} className="app-card px-3 py-1.5 rounded-lg font-semibold transition-all text-xs flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, color: colors.primaryText }}>
          Exit
        </button>
      </div>

      {/* Progress bar */}
      <div className="w-full h-1 rounded-full mb-4 overflow-hidden px-3" style={{ backgroundColor: colors.border }}>
        <div className="h-full transition-all duration-300"
          style={{ width: `${((currentCardIndex + 1) / totalCards) * 100}%`, backgroundColor: colors.accent }} />
      </div>

      {/* Carousel: full-width card with arrows overlaid on edges */}
      <div className="relative mx-0">
        <Card card={currentCard} />

        {/* Left arrow  -  floats over left edge, vertically centred */}
        {currentCardIndex > 0 && (
          <button
            onClick={handlePrevCard}
            aria-label="Previous card"
            className="absolute top-1/2 -translate-y-1/2 -left-3 flex items-center justify-center rounded-full z-10"
            style={{
              width: 45, height: 45,
              background: `${colors.accent}08`,
              border: `1px solid ${colors.border}`,
              color: colors.primaryText,
              boxShadow: `0 2px 10px rgba(0,0,0,0.15)`,
            }}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}

        {/* Right arrow  -  floats over right edge */}
        <button
          onClick={handleNextCard}
          aria-label="Next card"
          className="absolute top-1/2 -translate-y-1/2 -right-3 flex items-center justify-center rounded-full z-10"
          style={{
            width: 45, height: 45,
            background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`,
            border: `1px solid ${colors.border}`,
            color: colors.primaryText,
            boxShadow: `0 2px 10px rgba(0,0,0,0.25)`,
            transition: 'opacity 0.08s',
          }}
          onMouseDown={e => { e.currentTarget.style.opacity = '0.5'; }}
          onMouseUp={e => { e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
          onTouchStart={e => { e.currentTarget.style.opacity = '0.5'; }}
          onTouchEnd={e => { e.currentTarget.style.opacity = '1'; }}
        >
          {isLastCard
            ? <span className="text-[10px] font-bold leading-tight text-center">OK</span>
            : <ChevronRight className="w-6 h-6" />}
        </button>
      </div>

      {/* Dot indicators */}
      <div className="flex items-center justify-center gap-1.5 mt-4 mb-2 px-3">
        {renderDots()}
      </div>

      {/* Checkpoint next hint */}
      {upcomingCheckpoint && (
        <div className="flex justify-center mt-2 px-3">
          <span className="px-2 py-1 rounded-full text-[11px] font-medium"
            style={{ backgroundColor: `${colors.accent}12`, color: colors.accent, border: `1px solid ${colors.accent}30` }}>
            Checkpoint next
          </span>
        </div>
      )}
    </div>
  );
}

function Card({ card }) {
  const { colors } = useTheme();

  let resolvedVisual = null;
  if (card?.visual) {
    try {
      resolvedVisual = typeof card.visual === 'string' ? getVisualComponent(card.visual) : card.visual;
    } catch (e) {
      resolvedVisual = null;
    }
  }

  let renderedVisual = resolvedVisual;
  if (React.isValidElement(resolvedVisual)) {
    const existingClass = resolvedVisual.props?.className || '';
    renderedVisual = React.cloneElement(resolvedVisual, {
      className: `${existingClass} w-full max-w-full`.trim(),
      style: { maxHeight: 180, height: 'auto', display: 'block' },
      preserveAspectRatio: resolvedVisual.props?.preserveAspectRatio || 'xMidYMid meet'
    });
  }

  return (
    <div className="app-card border-y w-full overflow-hidden"
      style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderColor: colors.border }}>
      <div className="p-3.5 sm:p-4 space-y-2.5">
        <h2 className="text-base font-bold leading-tight" style={{ color: colors.primaryText }}>
          {card?.title}
        </h2>

        {resolvedVisual && (
          <div className="w-full flex items-center justify-center rounded-lg px-2 py-2"
            style={{ backgroundColor: `${colors.accent}08`, minHeight: 120, maxHeight: 200 }}>
            <div className="w-full max-w-[420px] flex items-center justify-center">
              {renderedVisual}
            </div>
          </div>
        )}

        <p className="text-sm leading-relaxed" style={{ color: colors.primaryText, whiteSpace: 'pre-line' }}>
          {card?.explanation || card?.summary}
        </p>

        <div className="rounded-lg p-3" style={{ backgroundColor: `${colors.accent}15` }}>
          <p className="text-xs font-semibold mb-1" style={{ color: colors.accent }}>KEY RULE</p>
          <p className="text-sm italic" style={{ color: colors.accent }}>{card?.rule}</p>
        </div>

        {(card?.example || card?.context) && (
          <div>
            <p className="text-xs font-semibold mb-1" style={{ color: colors.secondaryText }}>Example</p>
            <p className="text-sm leading-relaxed" style={{ color: colors.secondaryText, whiteSpace: 'pre-line' }}>
              {card.example || card.context}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function LevelCompleteScreen({ level, onBack }) {
  const { colors } = useTheme();
  return (
    <div className="w-full max-w-2xl mx-auto px-3 py-3">
      <div className="app-card rounded-xl border p-4 text-center" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderColor: colors.border }}>
        <div className="text-2xl mb-2">OK</div>
        <h2 className="text-sm font-bold mb-1.5" style={{ color: colors.primaryText }}>Level Complete</h2>
        <p className="text-xs mb-4" style={{ color: colors.secondaryText }}>You completed {level.name}</p>
        <button onClick={onBack} className="px-4 py-2 rounded-lg font-semibold text-xs"
          style={{ backgroundColor: colors.background, color: colors.primaryText, border: `1px solid ${colors.border}` }}>
          Back to Levels
        </button>
      </div>
    </div>
  );
}


 


